import { create } from 'zustand';

import { SwMessage } from '@/sw/protocol';

/*
 * Registering the service worker (`/sw.js`, src/sw) and telling the user when a new version of the app is waiting. The worker
 * only ever caches the app's shell and shows push notifications; a new build waits until the user chooses "Update" (or all
 * tabs are closed), so a page never runs next to a cache of another build.
 */

export const SERVICE_WORKER_URL = '/sw.js';

/** A tab looks for a new worker when it comes back to the foreground, at most this often (the browser checks at page loads). */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000;

interface UpdateState {
  /** The new worker that has installed and waits to take over (null: none). */
  waiting: ServiceWorker | null;
  setWaiting: (worker: ServiceWorker | null) => void;
}

export const usePwaUpdate = create<UpdateState>()((set) => ({
  waiting: null,
  setWaiting: (waiting) => set({ waiting }),
}));

export function serviceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/** The registration of this page's worker, once there is one. */
let current: ServiceWorkerRegistration | null = null;

export function currentRegistration(): ServiceWorkerRegistration | null {
  return current;
}

/**
 * Registers the worker once the page has loaded (so that it does not compete with the app's start). Without support — an
 * insecure address, an old browser — it does nothing: the app works the same, just not as an installable app with push.
 */
export function registerServiceWorker(): void {
  if (!serviceWorkerSupported()) return;
  const register = () => {
    navigator.serviceWorker
      .register(SERVICE_WORKER_URL, { scope: '/', updateViaCache: 'none' })
      .then(watch)
      .catch((error: unknown) => {
        console.warn('The service worker could not be registered', error);
      });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

function watch(registration: ServiceWorkerRegistration): void {
  current = registration;
  const announce = () => {
    // Only an update announces itself: the first worker of a page has nothing to replace.
    if (registration.waiting && navigator.serviceWorker.controller) {
      usePwaUpdate.getState().setWaiting(registration.waiting);
    }
  };
  announce();
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    installing?.addEventListener('statechange', () => {
      if (installing.state === 'installed') announce();
    });
  });

  let lastCheck = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || Date.now() - lastCheck < UPDATE_CHECK_INTERVAL_MS) return;
    lastCheck = Date.now();
    registration.update().catch(() => undefined);
  });
}

/** Activates the waiting worker; the page reloads when it has taken over. */
export function applyUpdate(): void {
  const { waiting } = usePwaUpdate.getState();
  if (!waiting) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
  waiting.postMessage({ type: SwMessage.skipWaiting });
}

/** Handlers of the messages the worker sends to this page. */
export interface WorkerMessageHandlers {
  /** Open this app path (return after navigating; the worker is told it worked). */
  navigate?: (path: string) => void;
  /** A push message arrived (the page may refresh what the notification is about). */
  push?: () => void;
  /** The browser replaced or dropped the push subscription. */
  subscriptionChanged?: () => void;
}

/** Listens to the worker's messages; returns the function that stops. */
export function listenToWorker(handlers: WorkerMessageHandlers): () => void {
  if (!serviceWorkerSupported()) return () => undefined;
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { type?: unknown; url?: unknown } | null;
    switch (data?.type) {
      case SwMessage.navigate:
        if (typeof data.url === 'string' && handlers.navigate) {
          handlers.navigate(data.url);
          // The worker waits for this answer; without it, it navigates the window itself.
          event.ports[0]?.postMessage('ok');
        }
        break;
      case SwMessage.push:
        handlers.push?.();
        break;
      case SwMessage.subscriptionChanged:
        handlers.subscriptionChanged?.();
        break;
    }
  };
  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
}
